import { type RefObject, useEffect, useRef } from 'react';
import { Box } from '@mui/material';

const WEIGHTS = [0.55, 0.85, 1, 0.85, 0.55];

/**
 * Five bars that follow the microphone level. Reads the level from a ref inside
 * a requestAnimationFrame loop so no React render happens per audio chunk.
 */
export function DictationLevelBars({
  levelRef,
  active,
  height = 16,
}: {
  levelRef: RefObject<number>;
  active: boolean;
  height?: number;
}) {
  const bars = useRef<(HTMLSpanElement | null)[]>([]);

  // Styling exception: the bar heights change every animation frame from the
  // live microphone level. Writing `transform` directly avoids a React render
  // per frame; every static style below stays in `sx`.
  useEffect(() => {
    const rest = () => bars.current.forEach(bar => bar && (bar.style.transform = 'scaleY(0.18)'));
    if (!active) {
      rest();
      return undefined;
    }
    let frame = 0;
    let smoothed = 0;
    const tick = (time: number) => {
      // Speech RMS sits around 0.05–0.25; scale it to fill the bars.
      const level = Math.min(1, levelRef.current * 5);
      smoothed += (level - smoothed) * 0.3;
      bars.current.forEach((bar, index) => {
        if (!bar) return;
        const wobble = 0.85 + 0.15 * Math.sin(time / 90 + index * 1.7);
        const scale = 0.18 + smoothed * WEIGHTS[index % WEIGHTS.length] * wobble * 0.82;
        bar.style.transform = `scaleY(${scale.toFixed(3)})`;
      });
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      rest();
    };
  }, [active, levelRef]);

  return (
    <Box
      aria-hidden="true"
      data-testid="dictation-level"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: '2px', height }}
    >
      {WEIGHTS.map((_, index) => (
        <Box
          key={index}
          component="span"
          ref={(element: HTMLSpanElement | null) => {
            bars.current[index] = element;
          }}
          sx={{
            display: 'block',
            width: 2,
            height: '100%',
            borderRadius: 1.5,
            bgcolor: 'error.main',
            transform: 'scaleY(0.18)',
            transformOrigin: 'center',
            transition: 'transform 70ms linear',
          }}
        />
      ))}
    </Box>
  );
}
